/**
 * Calendário no celular › "Sincronizar nos dois sentidos" (ADR-0093).
 *
 * Google Agenda: o ERP cria o calendário "Pharu ERP" na conta Google da
 * pessoa e só enxerga esse calendário. iPhone/Mac (CalDAV): a pessoa gera uma
 * senha de app e cadastra a conta no aparelho; a senha aparece uma vez.
 * O que muda lá volta pelas regras do ERP (as mesmas da tela), e a lista de
 * regras abaixo diz isso com todas as letras.
 */
import React, { useCallback, useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import BotaoCopiar from "../../../components/BotaoCopiar";
import ConfirmDialog from "../../../components/ConfirmDialog";
import {
  desconectarGoogle, desligarCalDAV, gerarSenhaCalDAV, iniciarGoogle, lerCalendarios, sincronizarGoogle,
} from "../../../services/agenda";
import { PASSOS_CALDAV } from "../conexoes/passos";

const formato = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
});
const instante = (iso) => (iso ? formato.format(new Date(iso)) : "");

export const REGRAS_DA_VIA_DUPLA = [
  "Comece o título com ✓ ou [x] para concluir; tire a marca para reabrir.",
  "Dia, horário e título voltam para o ERP. A descrição do evento é montada pelo ERP.",
  "Em tarefa compartilhada, só o dono reagenda: se outra pessoa mover lá, o evento volta e ela recebe um aviso.",
  "Apagar o evento lá fora não apaga no ERP: a tarefa do dono vai para a lixeira; quem participa sai dela.",
  "Evento criado no calendário “Pharu ERP” vira uma tarefa sua.",
  "Nenhum outro calendário seu é lido ou alterado.",
];

function PassosCalDAV({ servidor }) {
  const [aba, setAba] = useState(PASSOS_CALDAV[0].id);
  const atual = PASSOS_CALDAV.find((p) => p.id === aba);
  return (
    <>
      <div className="ia-abas" role="tablist" aria-label="Como conectar cada aparelho">
        {PASSOS_CALDAV.map((p) => (
          <button key={p.id} type="button" role="tab" aria-selected={p.id === aba} className={p.id === aba ? "ativa" : ""} onClick={() => setAba(p.id)}>
            {p.nome}
          </button>
        ))}
      </div>
      <ol key={aba} className="ia-passos" role="tabpanel">
        {atual.passos.map((passo) => <li key={passo}>{passo.replace("{servidor}", servidor)}</li>)}
      </ol>
    </>
  );
}

export function Credencial({ rotulo, valor }) {
  return (
    <div className="cal-credencial">
      <span>{rotulo}</span>
      <div className="ia-endereco">
        <code title={valor}>{valor}</code>
        <BotaoCopiar texto={valor} rotulo="Copiar" aria-label={`Copiar ${rotulo.toLowerCase()}`} />
      </div>
    </div>
  );
}

function situacaoDoGoogle(google) {
  if (!google.configurado) return "Ainda não configurado neste servidor.";
  if (!google.conectado) return "Cria o calendário “Pharu ERP” na sua conta Google e sincroniza nos dois sentidos.";
  const conta = google.conta ? `Conectado como ${google.conta}` : "Conectado";
  if (google.primeira_sincronizacao && !google.ultimo_erro) return `${conta} · primeira sincronização em andamento`;
  return `${conta}${google.ultimo_sync_em ? ` · sincronizado em ${instante(google.ultimo_sync_em)}` : ""}`;
}

function situacaoDoCalDAV(caldav) {
  if (!caldav.ativa) return "Para o app Calendário do iPhone, iPad e Mac (e Thunderbird ou DAVx⁵ no Android).";
  const uso = caldav.ultimo_uso_em ? `último uso ${instante(caldav.ultimo_uso_em)}` : "nenhum aparelho conectado ainda";
  return `Senha ${caldav.prefixo}… · criada em ${instante(caldav.criada_em)} · ${uso}`;
}

export default function SincronizacaoCalendarios({ request }) {
  const [estado, setEstado] = useState(null);
  const [senha, setSenha] = useState("");
  const [confirmar, setConfirmar] = useState(null);

  const carregar = useCallback(async () => {
    setEstado(await lerCalendarios(request).catch(() => null));
  }, [request]);
  useEffect(() => { carregar(); }, [carregar]);

  if (!estado?.google || !estado?.caldav) return null;
  const { google, caldav } = estado;

  const conectarGoogle = async () => {
    const { url } = await iniciarGoogle(request);
    window.location.assign(url);
  };
  const sincronizar = async () => setEstado(await sincronizarGoogle(request));
  const gerarSenha = async () => {
    const resposta = await gerarSenhaCalDAV(request);
    setSenha(resposta.caldav.senha);
    setEstado(resposta);
  };
  const confirmarAcao = async () => {
    if (confirmar === "google") await desconectarGoogle(request);
    if (confirmar === "caldav") { await desligarCalDAV(request); setSenha(""); }
    if (confirmar === "nova-senha") await gerarSenha();
    setConfirmar(null);
    if (confirmar !== "nova-senha") await carregar();
  };

  const DIALOGOS = {
    google: {
      titulo: "Desconectar o Google Agenda?",
      descricao: "O calendário “Pharu ERP” sai da sua conta Google e a sincronização para. Nada é apagado no ERP, nem o que foi criado pelo Google.",
      rotulo: "Desconectar",
    },
    caldav: {
      titulo: "Desligar o iPhone/Mac?",
      descricao: "A senha para de funcionar na hora e o calendário “Pharu ERP” deixa de atualizar nos aparelhos. Nada é apagado no ERP.",
      rotulo: "Desligar",
    },
    "nova-senha": {
      titulo: "Gerar outra senha?",
      descricao: "A senha atual para de funcionar na hora. Cada aparelho vai pedir a senha nova.",
      rotulo: "Gerar outra",
    },
  };
  const dialogo = DIALOGOS[confirmar];

  return (
    <>
      <section className="set-section">
        <div className="set-section__head">
          <h2 className="set-section__title">Sincronizar nos dois sentidos</h2>
          <p className="set-section__desc">Mexa pelo Google Agenda ou pelo Calendário do iPhone/Mac e a mudança volta para o ERP. O ERP continua sendo a referência.</p>
        </div>
        <div className="set-section__body">
          <ul className="ia-lista">
            <li className="ia-item">
              <div className="ia-item-t">
                <b>Google Agenda</b>
                <small title={situacaoDoGoogle(google)}>{situacaoDoGoogle(google)}</small>
                {google.ultimo_erro ? <small className="ia-erro" role="alert">{google.ultimo_erro}</small> : null}
              </div>
              {google.configurado && (!google.conectado || google.precisa_reconectar) ? (
                <AsyncButton variant="primary" onClick={conectarGoogle} silentSuccess silentError>
                  {google.precisa_reconectar ? "Conectar de novo" : "Conectar"}
                </AsyncButton>
              ) : null}
              {google.conectado && !google.precisa_reconectar ? (
                <AsyncButton variant="secondary" onClick={sincronizar} successMessage="Google Agenda sincronizado." silentError>
                  Sincronizar agora
                </AsyncButton>
              ) : null}
              {google.conectado ? (
                <button type="button" className="btn btn--ghost btn--sm ia-perigo" onClick={() => setConfirmar("google")}>Desconectar</button>
              ) : null}
            </li>
            <li className="ia-item">
              <div className="ia-item-t">
                <b>iPhone e Mac (CalDAV)</b>
                <small title={situacaoDoCalDAV(caldav)}>{situacaoDoCalDAV(caldav)}</small>
                {caldav.ultimo_erro ? <small className="ia-erro">{`Última recusa: ${caldav.ultimo_erro}`}</small> : null}
              </div>
              {caldav.ativa ? (
                <>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirmar("nova-senha")}>Gerar outra senha</button>
                  <button type="button" className="btn btn--ghost btn--sm ia-perigo" onClick={() => setConfirmar("caldav")}>Desligar</button>
                </>
              ) : (
                <AsyncButton variant="primary" onClick={gerarSenha} silentSuccess silentError>Gerar senha</AsyncButton>
              )}
            </li>
          </ul>

          {senha ? (
            <div className="ia-revelada cal-revelada" role="status">
              <b>Cadastre a conta no aparelho agora: a senha não aparece de novo.</b>
              <Credencial rotulo="Servidor" valor={caldav.servidor} />
              <Credencial rotulo="Nome de usuário" valor={caldav.usuario} />
              <Credencial rotulo="Senha" valor={senha} />
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSenha("")}>Já cadastrei</button>
            </div>
          ) : null}
          {caldav.ativa && !senha ? (
            <div className="cal-dados">
              <Credencial rotulo="Servidor" valor={caldav.servidor} />
              <Credencial rotulo="Nome de usuário" valor={caldav.usuario} />
              <p>Senha: a que apareceu ao gerar. Não anotou? Gere outra senha.</p>
            </div>
          ) : null}
          {caldav.ativa || senha ? <PassosCalDAV servidor={caldav.servidor} /> : null}
        </div>
      </section>

      <section className="set-section">
        <div className="set-section__head">
          <h2 className="set-section__title">Como a sincronização decide</h2>
        </div>
        <div className="set-section__body">
          <ul className="cal-regras">
            {REGRAS_DA_VIA_DUPLA.map((regra) => <li key={regra}>{regra}</li>)}
          </ul>
        </div>
      </section>

      <ConfirmDialog
        open={Boolean(dialogo)}
        titulo={dialogo?.titulo}
        descricao={dialogo?.descricao}
        confirmarLabel={dialogo?.rotulo}
        tone="danger"
        onConfirmar={confirmarAcao}
        onCancelar={() => setConfirmar(null)}
      />
    </>
  );
}
